import { IMapConfigEntry, IMapConfigEntryDomination, PlayerGrouping } from '@nova/types';

import { MapRepository } from './maps.repository';

export class MapService {
  private _mapRepo: MapRepository;

  public constructor(mapRepo?: MapRepository) {
    this._mapRepo = mapRepo || new MapRepository();
  }

  public async init(gameAPIEndpoint: string): Promise<void> {
    await this._mapRepo.init(gameAPIEndpoint);
  }

  public getAll(): (IMapConfigEntry | IMapConfigEntryDomination)[] {
    const mapsOr = this._mapRepo.getOrMapsCopied().filter((map) => map.isAvailable);
    const mapsDom = this._mapRepo.getDominationMapsCopied().filter((map) => map.isAvailable);
    const mapsTf = this._mapRepo.getTfMapsCopied().filter((map) => map.isAvailable);
    return [...mapsOr, ...mapsDom, ...mapsTf];
  }

  public findById(mapId: string): IMapConfigEntry | IMapConfigEntryDomination {
    const map = this._mapRepo.findMapByIdOr(mapId)
      || this._mapRepo.findByIdDomination(mapId)
      || this._mapRepo.findByIdTf(mapId);
    if (!map) { throw new Error(`Could not find map based on mapId <${mapId}>`); }
    return map;
  }

  /** Per-team cap for one lobby party (`map.teamSize`). Not total match capacity. */
  public getPlayerCap(map: IMapConfigEntry): number {
    return map.teamSize;
  }

  /** Largest per-team cap among all available maps. */
  public getTotalPlayerCap(): number {
    const maps = this.getAll();

    return this.getMaxPlayerCap(maps);
  }

  public getMultiPlayerMaps(): IMapConfigEntry[] {
    return this.getAll().filter((map) => map.playerGrouping !== PlayerGrouping.SP);
  }

  private getMaxPlayerCap(maps: IMapConfigEntry[]): number {
    let runningPlayerCap = 1;

    for (const map of maps) {
      const mapPlayerCap = this.getPlayerCap(map);

      if (mapPlayerCap > runningPlayerCap) {
        runningPlayerCap = mapPlayerCap;
      }
    }

    return runningPlayerCap;
  }

  /** Largest per-team cap among available multiplayer maps. */
  public getMultiPlayerPlayerCap(): number {
    const maps = this.getMultiPlayerMaps();
    return this.getMaxPlayerCap(maps);
  }

  /** Map IDs where a single lobby of `playerCount` fits within one team (`teamSize >= playerCount`). */
  public getMapIdsByPlayerCount(playerCount: number): string[] {
    const allMaps = this.getAll();
    const filteredMaps = allMaps.filter((map) => map.teamSize >= playerCount);
    return filteredMaps.map((map) => map.id);
  }

  /** True when one lobby's headcount (`playerCount`) does not exceed the map's per-team `teamSize`. */
  public isPlayerCountValid(mapId: string, playerCount: number): boolean {
    const map = this.findById(mapId);
    if (!map) {
      throw new Error(`MapService::isPlayerCountValid: Map ${mapId} not found`);
    }

    const playerCountOverTeamSize = playerCount > map.teamSize;
    if (playerCountOverTeamSize) {
      console.error(`MapService::isPlayerCountValid: Player count <${playerCount}> over team size <${map.teamSize}> for map <${mapId}>.`);
      return false;
    }

    return true;
  }
}
